import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useKid } from "@/context/KidContext";
import { CheckCircle2, PlayCircle, Lock } from "lucide-react";

export default function SeriesDetail() {
  const { key } = useParams();
  const { active } = useKid();
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = active ? `?child_id=${active.id}` : "";
    api.get(`/series/${key}${params}`).then(({ data }) => setData(data));
  }, [key, active]);

  if (!data) return <div className="p-10 text-center">Carregando…</div>;
  const { series, seasons, completed_count, total } = data;
  const pct = Math.round((completed_count / Math.max(1, total)) * 100);
  const nextEp = data.episodes?.find((e) => !e.completed) || null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-16">
      <Link to="/series" data-testid="series-back-link" className="text-[#5DADE2] font-bold text-sm">← Todas as séries</Link>

      <div className="mt-4 rounded-[2rem] overflow-hidden aspect-[16/7] bg-[#FFF9ED] relative">
        {series.cover_image ? (
          <img src={series.cover_image} alt={series.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl">📺</div>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 rainbow-strip opacity-90" />
      </div>

      <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold text-[#243B53]" data-testid="series-title">{series.title}</h1>
      <p className="mt-2 text-[#243B53]/80">{series.description}</p>

      <section className="mt-6 card-soft p-5" data-testid="series-progress">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-extrabold text-[#243B53]">Progresso</div>
          <div className="text-sm text-[#243B53]/70">{completed_count} de {total} episódios concluídos · <b>{pct}%</b></div>
        </div>
        <div className="mt-3 progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
        {nextEp && (
          <Link to={`/content/${nextEp.id}`} data-testid="series-next-episode-btn"
            className="btn-primary mt-4 inline-flex items-center gap-2 text-sm">
            <PlayCircle size={18} /> Continuar — {nextEp.title}
          </Link>
        )}
        {!nextEp && total > 0 && (
          <div className="mt-4 chip chip-gold">🏆 Série completa!</div>
        )}
      </section>

      <section className="mt-10">
        {seasons.map((s) => (
          <div key={s.season} className="mb-8">
            <h2 className="text-xl font-extrabold text-[#243B53]">Temporada {s.season}</h2>
            <ul className="mt-3 card-soft divide-y divide-[#243B53]/10 overflow-hidden">
              {s.episodes.map((ep, i) => (
                <li key={ep.id} data-testid={`episode-row-${ep.id}`}>
                  <Link to={`/content/${ep.id}`} className="flex items-center gap-4 p-4 hover:bg-[#FFF9ED]/60">
                    <div className="w-10 h-10 rounded-full bg-[#FFF9ED] flex items-center justify-center font-extrabold text-[#D9A441] shrink-0">
                      {ep.completed ? <CheckCircle2 className="text-[#7BC67B]" /> : (ep.episode_number ?? i+1)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-extrabold text-[#243B53] truncate">
                        Episódio {ep.episode_number ?? i+1} — {ep.title}
                      </div>
                      <div className="text-xs text-[#243B53]/60 truncate">{ep.description}</div>
                    </div>
                    <div className="text-xs text-[#243B53]/60 hidden sm:block">
                      {ep.duration_min ? `${ep.duration_min} min` : ""}
                    </div>
                    {ep.completed ? (
                      <span className="chip chip-leaf hidden sm:inline-block">Concluído</span>
                    ) : (
                      <PlayCircle className="text-[#5DADE2]" size={22} />
                    )}
                  </Link>
                </li>
              ))}
              {s.episodes.length === 0 && <li className="p-4 text-sm text-[#243B53]/60">Nenhum episódio ainda.</li>}
            </ul>
          </div>
        ))}
        {seasons.length === 0 && (
          <div className="card-soft p-8 text-center text-[#243B53]/60">
            <Lock className="mx-auto text-[#D9A441]" /> Ainda não há episódios nesta série.
          </div>
        )}
      </section>
    </div>
  );
}
